import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChatButtonAccent } from '@/lib/chat-customization';
import { getContrastForeground, LAUNCHER_FOREGROUND_RATIO, WCAG_AA_LARGE } from '@/lib/brand-color-accessibility';

const FAB_SHADOW = '0 4px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.25)';

/** Default widget FAB dimensions. */
export const LAUNCHER_FAB_SIZE = 52;

/** Chat bubble icon size on the canonical 52px FAB (all accent modes). */
const LAUNCHER_ICON_SIZE = 24;

/** Puck fills ~88% of FAB height — matches design reference. */
const PUCK_TO_FAB_RATIO = 46 / LAUNCHER_FAB_SIZE;

function scaleLauncherIcon(fabSize: number): number {
  return Math.round(LAUNCHER_ICON_SIZE * fabSize / LAUNCHER_FAB_SIZE);
}

export interface LauncherIconMetrics {
  puckSize: number;
  iconSize: number;
}

export function getLauncherIconMetrics(fabSize: number, hasPuck: boolean): LauncherIconMetrics {
  const iconSize = scaleLauncherIcon(fabSize);

  if (hasPuck) {
    return { puckSize: Math.round(fabSize * PUCK_TO_FAB_RATIO), iconSize };
  }

  return { puckSize: 0, iconSize };
}

export interface LauncherAccentStyleInput {
  accent: ChatButtonAccent;
  primary: string;
  secondary: string;
}

export interface LauncherAccentStyles {
  backgroundColor: string;
  color: string;
  border?: string;
  iconColor: string;
  labelColor: string;
  iconPuck?: {
    backgroundColor: string;
    color: string;
  };
}

export function resolveLauncherAccentStyles({
  accent,
  primary,
  secondary,
}: LauncherAccentStyleInput): LauncherAccentStyles {
  const secondaryColor = secondary || primary;

  if (accent === 'primary_only') {
    const foreground = getContrastForeground(primary, LAUNCHER_FOREGROUND_RATIO);
    return {
      backgroundColor: primary,
      color: foreground,
      iconColor: foreground,
      labelColor: foreground,
    };
  }

  if (accent === 'accent_icon') {
    const labelColor = getContrastForeground(primary, LAUNCHER_FOREGROUND_RATIO);
    const puckForeground = getContrastForeground(secondaryColor, WCAG_AA_LARGE);
    return {
      backgroundColor: primary,
      color: labelColor,
      iconColor: puckForeground,
      labelColor,
      iconPuck: {
        backgroundColor: secondaryColor,
        color: puckForeground,
      },
    };
  }

  const foreground = getContrastForeground(primary, LAUNCHER_FOREGROUND_RATIO);
  return {
    backgroundColor: primary,
    color: foreground,
    border: `2px solid ${secondaryColor}`,
    iconColor: foreground,
    labelColor: foreground,
  };
}

export function getLauncherFabSx(
  accent: ChatButtonAccent,
  primary: string,
  secondary: string,
): SxProps<Theme> {
  const styles = resolveLauncherAccentStyles({ accent, primary, secondary });
  return {
    bgcolor: styles.backgroundColor,
    color: styles.color,
    ...(styles.border ? { border: styles.border, boxSizing: 'border-box' as const } : {}),
    boxShadow: FAB_SHADOW,
    transition: 'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease, transform 160ms ease',
    '&:hover': {
      bgcolor: styles.backgroundColor,
      ...(styles.border ? { border: styles.border } : {}),
    },
    '&:active': {
      transform: 'translateY(1px)',
      boxShadow: '0 3px 9px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.3)',
    },
  };
}

/** Mini preview styles for settings accent cards (non-MUI). */
export function getLauncherPreviewStyles(
  accent: ChatButtonAccent,
  primary: string,
  secondary: string,
  showLabel: boolean,
): CSSProperties & { iconPuck?: CSSProperties; labelColor?: string; iconColor?: string } {
  const resolved = resolveLauncherAccentStyles({ accent, primary, secondary });
  const base: CSSProperties & {
    iconPuck?: CSSProperties;
    labelColor?: string;
    iconColor?: string;
  } = {
    backgroundColor: resolved.backgroundColor,
    color: resolved.color,
    ...(resolved.border ? { border: resolved.border, boxSizing: 'border-box' } : {}),
    iconColor: resolved.iconColor,
    labelColor: resolved.labelColor,
  };

  if (resolved.iconPuck) {
    const { puckSize } = getLauncherIconMetrics(showLabel ? 36 : 32, true);
    base.iconPuck = {
      backgroundColor: resolved.iconPuck.backgroundColor,
      color: resolved.iconPuck.color,
      width: puckSize,
      height: puckSize,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    };
  }

  return base;
}

export function getLabelContrastOnPrimary(primary: string): string {
  return getContrastForeground(primary, LAUNCHER_FOREGROUND_RATIO);
}
